import { useQuery } from "@tanstack/react-query";
import { Bot, type LucideIcon } from "lucide-react";
import { api } from "../../api";
import { config } from "../../config";
import { actorName } from "../../lib/people";
import type { Tone } from "../../lib/status";
import { useFactory } from "../../store";
import { hasIntegrationLogo, IntegrationLogo } from "../integration-logo";
import { StatusDot, ToneBadge } from "../status-badge";
import { TimeAgo } from "../time-ago";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { ScrollArea } from "../ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { DisplayCard } from "./display-card";
import { Endpoint } from "./endpoint";

interface Connection {
  label: string;
  detail: string;
  /** Product mark id when this service has one. */
  logo?: string;
  icon?: LucideIcon;
  tone: Tone;
  status: string;
}

export function GeneralTab() {
  const { state, layout, realtime } = useFactory();
  const audit = useQuery({ queryKey: ["audit"], queryFn: () => api.audit(60), refetchInterval: 30_000 });
  const deliveries = useQuery({
    queryKey: ["deliveries"],
    queryFn: () => api.trackerDeliveries(30),
    refetchInterval: 30_000,
  });
  const apiBase = config.apiUrl.startsWith("http")
    ? config.apiUrl
    : `${window.location.origin}${config.apiUrl}`;

  const trackerId = state?.tracker.id ?? layout?.tracker.provider ?? "skynet";
  const scmId = state?.scm.id ?? "github";
  const connections: Connection[] = [
    {
      label: "Issue tracker",
      detail: `${trackerId} (built in)`,
      logo: hasIntegrationLogo(trackerId) ? trackerId : undefined,
      tone: "emerald",
      status: "Native",
    },
    {
      label: "Source control",
      detail: scmId,
      logo: hasIntegrationLogo(scmId) ? scmId : undefined,
      tone: state?.scm.configured ? "emerald" : "amber",
      status: state?.scm.configured ? "Configured" : "Missing token, merges simulated",
    },
    ...Object.entries(state?.providers ?? {}).map(
      ([id, p]): Connection => ({
        label: "Agent provider",
        detail: id,
        logo: hasIntegrationLogo(id) ? id : undefined,
        icon: hasIntegrationLogo(id) ? undefined : Bot,
        tone: p.configured ? "emerald" : "amber",
        status: p.configured ? "Configured" : "Not configured",
      }),
    ),
    {
      label: "Realtime feed",
      detail: "AWS IoT websocket",
      logo: "aws",
      tone: realtime === "connected" ? "emerald" : realtime === "disabled" ? "sky" : "amber",
      status: realtime === "disabled" ? "Polling" : realtime.charAt(0).toUpperCase() + realtime.slice(1),
    },
  ];

  return (
    <div className="grid gap-6 @5xl/main:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Connections</CardTitle>
          <CardDescription>Services the factory depends on</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="divide-y">
            {connections.map((c) => (
              <li
                key={`${c.label}-${c.detail}`}
                className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
              >
                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg border bg-background">
                  {c.logo ? (
                    <IntegrationLogo id={c.logo} className="size-5" />
                  ) : (
                    c.icon && <c.icon className="size-4 text-muted-foreground" />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{c.label}</p>
                  <p className="truncate font-mono text-xs text-muted-foreground">{c.detail}</p>
                </div>
                <ToneBadge tone={c.tone} className="gap-1.5">
                  <StatusDot tone={c.tone} pulse={c.tone === "emerald" && c.label === "Realtime feed"} />
                  {c.status}
                </ToneBadge>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <div className="flex flex-col gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Endpoints</CardTitle>
            <CardDescription>Webhooks and the Skynet Issues MCP server</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Endpoint
              label="GitHub App"
              logo="github"
              url={`${apiBase}/webhooks/github`}
              hint="Skynet GitHub App webhook. Events: pull_request, pull_request_review, check_suite, workflow_run, status, release. Secret: SkynetGithubWebhookSecret. App writes: pull requests, contents (branches, releases), issues, checks."
            />
            <Endpoint
              label="Sentry"
              logo="sentry"
              url={`${apiBase}/webhooks/sentry`}
              hint="Sentry internal integration webhook. Add the integration as an issue alert action; each Sentry issue creates one bug. Secret: SkynetSentryWebhookSecret (the integration's Client Secret)."
            />
            <Endpoint
              label="Slack"
              logo="slack"
              url={`${apiBase}/webhooks/slack`}
              hint="Slack Events API request URL. A message to the Skynet agent, or a threaded @mention, files an issue. Secrets: SkynetSlackSigningSecret and SkynetSlackBotToken."
            />
            <Endpoint
              label="Skynet Issues MCP"
              url={`${apiBase}/mcp`}
              hint="Streamable HTTP. Built in and always connected. Agent runs receive a run-scoped token automatically."
            />
            <Endpoint label="MCP OAuth redirect" url={`${apiBase}/mcp/oauth/callback`} />
          </CardContent>
        </Card>
        <DisplayCard />
      </div>

      <Card className="gap-0 pb-0">
        <CardHeader className="border-b">
          <CardTitle>Webhook deliveries</CardTitle>
          <CardDescription>The latest events received from the tracker</CardDescription>
        </CardHeader>
        <ScrollArea className="h-80">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-4">When</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Event</TableHead>
                <TableHead className="pr-4 text-right">Result</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(deliveries.data?.deliveries ?? []).map((d) => (
                <TableRow key={d.id}>
                  <TableCell className="pl-4 text-muted-foreground tabular-nums">
                    <TimeAgo at={d.createdAt} />
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5">
                      {hasIntegrationLogo(d.provider) && (
                        <IntegrationLogo id={d.provider} className="size-3.5" />
                      )}
                      {d.provider}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-48 truncate font-mono text-xs">
                    {d.type} {d.action}
                  </TableCell>
                  <TableCell className="pr-4 text-right">
                    {d.error ? (
                      <ToneBadge tone="red" title={d.error}>
                        Error
                      </ToneBadge>
                    ) : d.processedAt ? (
                      <ToneBadge tone="emerald">OK</ToneBadge>
                    ) : (
                      <ToneBadge tone="amber">Pending</ToneBadge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {deliveries.data?.deliveries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                    No deliveries yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </Card>

      <Card className="gap-0 pb-0">
        <CardHeader className="border-b">
          <CardTitle>Audit log</CardTitle>
          <CardDescription>Who changed what, most recent first</CardDescription>
        </CardHeader>
        <ScrollArea className="h-80">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-4">When</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Action</TableHead>
                <TableHead className="pr-4">Target</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(audit.data?.entries ?? []).map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="pl-4 text-muted-foreground tabular-nums">
                    <TimeAgo at={e.createdAt} />
                  </TableCell>
                  <TableCell className="max-w-40 truncate">{actorName(e.actor) ?? "Someone"}</TableCell>
                  <TableCell className="font-mono text-xs">{e.action}</TableCell>
                  <TableCell className="max-w-48 truncate pr-4 text-muted-foreground">{e.target}</TableCell>
                </TableRow>
              ))}
              {audit.data?.entries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                    Nothing recorded yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </Card>
    </div>
  );
}
